/**
 * tm/portal-ui/portalLogic.ts — FP 포털 · 고객 페이지 공용 계산 (2026-10-12 웹 시안 3판 · 7곳 검수 반영)
 *  - 날짜는 한국 날짜 기준
 *  - 대표 배지 = 가장 급한 것: 지남 > 회신 기다림 > 0~3일 > 4~7일 > 8일 이상 (업체 · 업무 모두 같은 규칙)
 *  - 업무 배지 = 다음 마감(남은 단계 중 가장 이른 기한, 없으면 업무 기한)
 *  - 회신 배지 = 고객 · FP 모두 「답 기다린 지 n일」(2026-10-12 UI/UX 검수: 화면마다 낱말이 달랐음 · FP 배지는 주어가 없어 거꾸로 읽힘)
 *  - 날짜: 지도사 단계 목표일 · 업무 기한은 서버가 아예 안 보낸다 — 단계 due 는 📌 공고 마감(fixed)일 때만, 끝난 단계는 done_at
 */

export type Audience = 'customer' | 'fp';

export interface PStep {
  id: string;
  name: string;
  status: string;            // 대기 · 진행 · 회신대기 · 완료
  due: string | null;
  sort_order?: number;
  wait_for?: string[];       // ['업체','FP'] — 누구든 먼저 답하면 끝
  wait_since?: string | null;
  wait_question?: string;
  /** 답 기다림 단계에서 아직 안 받은 서류(이미 받은 것은 서버가 뺌 · 2026-10-28 services/stepOwner) */
  wait_items?: string[];
  replied_by?: string;
  replied_at?: string | null;
  fixed?: boolean;           // 📌 공고 마감(밀리지 않는 날) — 이때만 due 가 온다
  collect?: boolean;         // 자료 받기 단계(자료수집이 걸림) — 「답하기」가 아니라 「자료 보내기」로(2026-10-27)
  claimed_by?: string;       // 「답하기」를 누른 사람(업체 · FP) — 지도사가 확인하기 전까지
  claimed_at?: string | null;
  done_at?: string | null;   // 끝난 날
  later?: boolean;           // 진행으로 잡혀 있지만 아직 시작 전(지도사 일정이 뒤 — 날짜는 안 보냄 · 2026-10-13 지도사 「하고 있는 게 아니라 대기 중」)
  /** 결과 단계(2026-10-18) — yes 승인 · 선정 · 통과 / no 미○○ · retry_on = 다시 신청하기로 한 날 */
  result?: 'yes' | 'no';
  result_word?: string;
  retry_on?: string | null;
  /** 다시 신청 단계 · retry_month = 날짜가 아니라 「11월 예정」으로만 보인다 */
  retry?: boolean;
  retry_month?: boolean;
  /** 대출 「대표님 직접 접수」(2026-10-30 · services/loanSteps) — 방문 · 온라인 · 접수처 · 온라인 주소 → 대표님 화면 「접수했어요」 */
  self?: { how: 'visit' | 'online'; where: string; url: string };
}

/** 대출 결과(2026-10-14 · 서버 loanBoard.parseTerms 와 같은 모양) */
export interface LoanTerms { amount: number | null; rate: string; when: string; state: 'guess' | 'set' | 'done'; at?: string | null; lender?: string }   // lender = 실행 은행 · 기관(2026-10-28)
/** 만원 → 「3억원」 「1억 5,000만원」 「5,000만원」 */
export function wonText(man: number | null): string {
  if (man == null) return '';
  const e = Math.floor(man / 10000), r = man % 10000;
  return e ? `${e}억${r ? ` ${r.toLocaleString('ko-KR')}만` : ''}원` : `${r.toLocaleString('ko-KR')}만원`;
}

export interface PTask {
  id: string;
  company_id?: string;
  type: string;
  icon?: string;
  status: string;            // 진행 · FP요청대기 · 보류 · 중단 · 완료
  deadline: string | null;
  steps: PStep[];
  stop_reason?: string | null;
  /** 대출 점검 최근 소식(2026-10-13) — 직원이 매일 적는 한 줄 */
  news?: { day: string; text: string } | null;
  /** 대출 결과(2026-10-14) — 금액(만원) · 금리 · 실행일 · 대략 예상/확정/실행 완료 */
  terms?: LoanTerms | null;
  /** 이번 회차 마감(심사 미선정 · 2026-10-12) — 「멈춤」 대신 부드러운 문구 */
  round_closed?: boolean;
  /** 대출 갈래(신청한 곳) — 「다음 공고」 말 대신 대출 말로(2026-10-27) */
  loan?: boolean;
  /** 대출이 아직 진행 중 — 선정 안 된 곳도 「끝났거나 멈춘 일」로 보내지 않고 대책과 함께 진행 칸에(2026-10-27 지도사 「저 밑에 끝났거나 멈춘 일이 나오네 이상하다」) */
  loan_open?: boolean;
  /** 선정 안 됨 대책 — ① 왜 안 됐는지 ② 보완할 점 ③ 다음 신청(지도사 「고객이 납득가능하도록」) */
  round_result?: RoundResult | null;
  stopped_at?: string | null;
  done_at?: string | null;
}

export type BadgeTone = 'elephant' | 'blue' | 'yellow' | 'red' | 'teal' | 'green' | 'stop';
export interface BadgeSpec { text: string; tone: BadgeTone }

export type UrgKind = 'over' | 'wait' | 'left' | 'none';
export interface Urgency { kind: UrgKind; days: number; step: PStep | null }

const WD = ['일', '월', '화', '수', '목', '금', '토'];

/** 오늘(한국) YYYY-MM-DD */
export function kstToday(now: number = Date.now()): string {
  return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

export function d10(v: string | null | undefined): string {
  return v ? String(v).slice(0, 10) : '';
}

/** b - a (일) */
export function daysBetween(a: string, b: string): number {
  const ta = Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10));
  const tb = Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10));
  return Math.round((tb - ta) / 86400000);
}

function weekday(d: string): string {
  return WD[new Date(Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10))).getUTCDay()];
}

/** 10/16(금) */
export function md(v: string | null | undefined): string {
  const d = d10(v);
  if (!d) return '';
  return `${+d.slice(5, 7)}/${+d.slice(8, 10)}(${weekday(d)})`;
}

/** 10월 16일(금) */
export function mdLong(v: string | null | undefined): string {
  const d = d10(v);
  if (!d) return '';
  return `${+d.slice(5, 7)}월 ${+d.slice(8, 10)}일(${weekday(d)})`;
}

/** 10월 12일(월) 오전 9시 기준이에요 */
export function asOfLabel(iso: string | null | undefined, now: number = Date.now()): string {
  const t = iso ? new Date(iso).getTime() : now;
  const k = new Date(t + 9 * 3600 * 1000);
  const h = k.getUTCHours();
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const day = k.toISOString().slice(0, 10);
  return `${mdLong(day)} ${ampm} ${h12}시 기준이에요`;
}

/** 이번 회차 마감 문구 — 대표님 · 팀장님이 난감하지 않게(「탈락」 · 이유 없음 · 2026-10-12) */
export const ROUND_CLOSED_TEXT = '이번 회차는 선정되지 않았어요. 다음 공고가 열리면 먼저 안내드릴게요';
export interface RoundResult { reason: string; improve: string; next: string }
/** 마감 문구 — 정부지원사업은 「다음 공고」, 대출은 공고가 없으니 「이번엔 선정되지 않았어요」(2026-10-27) */
export function roundClosedText(t: PTask): string { return t.loan ? '이번엔 선정되지 않았어요' : ROUND_CLOSED_TEXT; }
/** 진행 중인 대출의 선정 안 된 곳은 끝난 일이 아니다 — 대책과 함께 진행 칸에 */
export function isEnded(t: PTask): boolean { return t.status === '완료' || (t.status === '중단' && !t.loan_open); }

export function waitStep(t: PTask): PStep | null {
  return t.steps.find(s => s.status === '회신대기') || null;
}

/** 지금 단계 — 회신대기 > 진행 > 아직 안 끝난 첫 단계 */
export function currentStep(t: PTask): PStep | null {
  return waitStep(t) || t.steps.find(s => s.status === '진행') || t.steps.find(s => s.status !== '완료') || null;
}

/** 아직 시작 전인 단계 — 대기 · 일정이 뒤인 진행 · 업무가 팀장님 요청 대기 (2026-10-13 지도사 「바로 시작 안 하고 대기 중인데 하고 있어요로 보임」) */
export function notStarted(t: PTask, s: PStep | null): boolean {
  if (!s || s.status === '완료' || s.status === '회신대기') return false;
  return s.status === '대기' || !!s.later || t.status === 'FP요청대기';
}

/** 이 사람에게 답을 기다리는 단계인가 */
export function waitsOn(s: PStep, who: Audience): boolean {
  if (s.status !== '회신대기') return false;
  const w = s.wait_for && s.wait_for.length ? s.wait_for : ['업체', 'FP'];
  return who === 'customer' ? w.includes('업체') : w.includes('FP');
}

/** 다음 마감 단계 — 남은 단계 중 기한이 가장 이른 것 */
export function nextDueStep(t: PTask): PStep | null {
  // 마감 배지는 📌 공고 마감만 — 지도사 목표일(2026-10-13부터 화면에 보임)은 늦어져도 「지남」 배지를 붙이지 않는다
  const open = t.steps.filter(s => s.status !== '완료' && s.due && s.fixed);
  if (!open.length) return null;
  return open.slice().sort((a, b) => d10(a.due).localeCompare(d10(b.due)))[0];
}

export function urgency(t: PTask, who: Audience, today: string = kstToday()): Urgency {
  const w = waitStep(t);
  const nd = nextDueStep(t);
  const ndDate = nd ? d10(nd.due) : d10(t.deadline);
  if (ndDate && daysBetween(today, ndDate) < 0) return { kind: 'over', days: -daysBetween(today, ndDate), step: nd };
  // 고객에게는 FP 만 기다리는 회신은 대표님 할 일이 아니다 — 마감으로 본다
  if (w && (who === 'fp' || waitsOn(w, 'customer'))) {
    const since = d10(w.wait_since) || today;
    return { kind: 'wait', days: Math.max(0, daysBetween(since, today)), step: w };
  }
  if (ndDate) return { kind: 'left', days: daysBetween(today, ndDate), step: nd };
  return { kind: 'none', days: 0, step: null };
}

/** 정렬 열쇠 — 작을수록 급함 */
export function urgencyRank(u: Urgency): [number, number] {
  if (u.kind === 'over') return [0, -u.days];
  if (u.kind === 'wait') return [1, -u.days];
  if (u.kind === 'left') return [u.days <= 3 ? 2 : u.days <= 7 ? 3 : 4, u.days];
  return [5, 0];
}

export function compareUrgency(a: Urgency, b: Urgency): number {
  const ra = urgencyRank(a); const rb = urgencyRank(b);
  return ra[0] - rb[0] || ra[1] - rb[1];
}

export function waitBadgeText(days: number, _who?: Audience): string {   // 고객 · FP 같은 말
  // 「오늘 여쭤봤어요」는 무엇을 보낸 것처럼 읽혔다 — 회신 대기는 표시일 뿐 문자가 나가지 않는다(2026-10-18 지도사 「아직 뭘 보낸 건 없는데?」)
  return days === 0 ? '답 기다려요' : `답 기다린 지 ${days}일`;
}

export function leftBadge(days: number, word: '남음' | '뒤' = '남음'): BadgeSpec {
  if (days < 0) return { text: `${-days}일 지남`, tone: 'red' };
  // 상담 약속(「n일 뒤」)은 경고가 아니다 — 파랑 고정(노랑은 임박 경고 · 카카오 버튼과 겹침)
  if (word === '뒤') return { text: days === 0 ? '오늘' : `${days}일 뒤`, tone: 'blue' };
  if (days === 0) return { text: '오늘', tone: 'yellow' };
  return { text: `${days}일 ${word}`, tone: days <= 3 ? 'yellow' : days <= 7 ? 'blue' : 'elephant' };
}

/** 마감 배지 — 이제 마감 = 📌 공고 마감뿐이라 「마감」을 붙여 말한다 */
export function badgeOf(u: Urgency, who: Audience): BadgeSpec | null {
  if (u.kind === 'over') return { text: `마감 ${u.days}일 지남`, tone: 'red' };
  if (u.kind === 'wait') return { text: waitBadgeText(u.days, who), tone: 'teal' };
  if (u.kind === 'left') return u.days === 0 ? { text: '오늘 마감', tone: 'yellow' } : { text: `마감 ${u.days}일 전`, tone: u.days <= 3 ? 'yellow' : u.days <= 7 ? 'blue' : 'elephant' };
  return null;
}

/** 업무 배지 — 끝난 · 멈춘 일은 완료 · 중단, 보류는 회색 「보류」 */
export function taskBadge(t: PTask, who: Audience, today: string = kstToday()): BadgeSpec | null {
  if (t.status === '완료') return { text: '완료', tone: 'green' };
  if (t.status === '중단' && t.round_closed && t.loan_open) return { text: '다음 신청 준비', tone: 'blue' };
  if (t.status === '중단' && t.round_closed) return { text: t.loan ? '선정 안 됨' : '이번 회차 마감', tone: 'elephant' };
  if (t.status === '중단') return { text: '멈춤', tone: 'stop' };
  if (t.status === '보류') return { text: '보류', tone: 'elephant' };
  const u = urgency(t, who, today);
  // 누가 답을 보냈다고 했으면 「답 확인 중」(늘어나는 「답 기다린 지 n일」에 또 답하지 않게)
  if (u.kind === 'wait' && u.step && u.step.claimed_by) return { text: '답 확인 중', tone: 'elephant' };
  // 팀장님 목록: 대표님 답만 기다리는 일은 회색(청록 = 팀장님도 답할 수 있는 일)
  if (who === 'fp' && u.kind === 'wait' && u.step && !waitsOn(u.step, 'fp')) return { text: waitBadgeText(u.days), tone: 'elephant' };
  // 고객에게는 「대표님 답을 기다려요」만 — 지도사 단계 기한 · 지남은 보이지 않는다(대표님 할 일이 아님 · 늦어진 내부 일정이 불안을 줌)
  if (who === 'customer' && u.kind !== 'wait') return null;
  return badgeOf(u, who);
}

/** 업체 대표 — 진행 중인 업무 중 가장 급한 것 하나 */
export function representative(tasks: PTask[], who: Audience, today: string = kstToday()): { task: PTask; urg: Urgency } | null {
  const act = tasks.filter(t => !isEnded(t) && t.status !== '보류');
  if (!act.length) return null;
  return act.map(task => ({ task, urg: urgency(task, who, today) })).sort((a, b) => compareUrgency(a.urg, b.urg))[0];
}

/** 누구 답을 기다리나 — FP 화면에서는 FP 를 「팀장님」으로 부른다(「FP님」 X · 지도사 2026-10-12) */
function whoWaits(s: PStep, who: Audience, fpTitle = '팀장'): string {
  const w = s.wait_for && s.wait_for.length ? s.wait_for : ['업체', 'FP'];
  if (who === 'customer') return w.includes('업체') ? '대표님' : `담당 ${fpTitle}님`;
  if (w.includes('업체') && w.includes('FP')) return `대표님이나 ${fpTitle}님`;
  return w.includes('업체') ? '대표님' : `${fpTitle}님`;
}

/** 을 · 를 — 끝 글자 받침 */
export function eulReul(word: string): string {
  const ch = (word || '').trim().slice(-1);
  const code = ch.charCodeAt(0) - 0xAC00;
  if (code < 0 || code > 11171) return '을';
  return code % 28 === 0 ? '를' : '을';
}

/** 결과 낱말 → 문장 (2026-10-18 · 「탈락 · 실패」는 쓰지 않는다) */
export function resultSentence(word: string | undefined, yes: boolean): string {
  const w = String(word || '');
  if (yes) return w === '승인' ? '승인됐어요' : w === '통과' ? '통과했어요' : w === '선정' ? '선정됐어요' : '끝났어요';
  return w === '미승인' ? '승인되지 않았어요' : w === '미통과' ? '통과하지 못했어요' : '선정되지 않았어요';
}

/** 「11월」 · 해가 바뀌면 「2027년 1월」 */
function monthText(ymd: string, today: string = kstToday()): string {
  const y = ymd.slice(0, 4), m = +ymd.slice(5, 7);
  return y === today.slice(0, 4) ? `${m}월` : `${y}년 ${m}월`;
}

/** 다시 신청을 기다리는 업무 — 마지막 「미○○」 결과 + 그 뒤 다시 신청 단계가 아직 시작 전 */
export function retryPlan(t: PTask, today: string = kstToday()): { miss: PStep; next: PStep; when: string } | null {
  const ss = t.steps;
  let mi = -1;
  ss.forEach((s, i) => { if (s.status === '완료' && s.result === 'no' && s.retry_on) mi = i; });
  if (mi < 0) return null;
  const next = ss.slice(mi + 1).find(s => s.retry && s.status !== '완료');
  if (!next || next.status === '회신대기' || (next.status === '진행' && !next.later)) return null;
  const day = d10(next.due) || d10(ss[mi].retry_on);
  return { miss: ss[mi], next, when: day ? monthText(day, today) : '' };
}

/** 단계 줄 모양 — 끝남 · 결과 미○○(회색 채운 원 · 가로줄) · 회신 대기 · 지금 · 앞으로 / 다시 신청 단계는 시작 전이어도 「지금」처럼 강조(다음 할 일로 시선 · 2026-10-18 검수) */
export function stepState(t: PTask, s: PStep): 'done' | 'miss' | 'wait' | 'now' | 'plan' {
  if (s.status === '완료') return s.result === 'no' ? 'miss' : 'done';
  if (s.status === '회신대기') return 'wait';
  if (t.status === 'FP요청대기') return 'plan';
  if (s.status === '진행' && !s.later) return 'now';
  return s.retry && currentStep(t) === s ? 'now' : 'plan';
}

/** 업무 카드 「지금은 …」 한 줄 */
export function nowLine(t: PTask, who: Audience, jido: string, fpTitle = '팀장'): { text: string; wait: boolean; full?: boolean } {
  if (t.status === '보류') return { text: '잠시 멈춰 두었어요', wait: false };
  if (t.status === '중단' && t.round_closed && t.loan) return { text: t.round_result ? `이번엔 선정되지 않았어요. 보완해서 다음 신청을 ${jido}가 준비해${who === 'customer' ? ' 드려' : ''}요` : '이번엔 선정되지 않았어요', wait: false, full: true };
  const w = waitStep(t);
  if (w && w.claimed_by) return { text: `${jido}가 보내 주신 답을 확인하고 있어요`, wait: true };
  if (w) return { text: `${whoWaits(w, who, fpTitle)} 답을 기다려요`, wait: true };
  // 결과가 「미○○」이고 다시 신청하기로 함 — 「이번엔 승인되지 않았어요. 11월에 임지도사가 다시 신청해 드려요」(누가 하는지까지 · 2026-10-18 검수)
  const rp = retryPlan(t);
  if (rp) return { text: `이번엔 ${resultSentence(rp.miss.result_word, false)}. ${rp.when ? rp.when + '에 ' : ''}${jido}가 다시 신청해${who === 'customer' ? ' 드려' : ''}요`, wait: false, full: true };
  const cur = currentStep(t);
  if (!cur) return { text: '마무리하고 있어요', wait: false };
  if (notStarted(t, cur)) return { text: `「${short(cur.name, 14).replace(/ /g, '\u00A0')}」\u2060${eulReul(cur.name)} 시작하기 전이에요`, wait: false };
  // 「단계」와 조사를 한 덩이로(」 뒤에서 줄이 끊겨 조사가 줄 앞에 오던 것 · 2026-10-12 2차 검수)
  // 누가 하는지는 단정하지 않는다 — 현장 평가 · 심사는 기관이 하는 일(2026-10-28 지도사 「임지도사가 현장평가를 하는 게 아니잖아 · 『현장 평가 단계예요』 이런 식으로」)
  return { text: `「${short(cur.name, 14).replace(/ /g, '\u00A0')}」\u2060단계예요`, wait: false };
}

/** 「지금은 …」 문장 — 다시 신청 안내처럼 그 자체로 한 문장이면 머리말 없이 */
export function nowSentence(nl: { text: string; full?: boolean }): string { return nl.full ? nl.text : `지금은 ${nl.text}`; }

/** 단계 줄 설명 */
/** 단계 줄 설명 — 기다린 날수는 배지에만(한 카드에 같은 말 세 번 X · 2026-10-12 검수) */
export function stepNote(s: PStep, who: Audience, jido: string, fpTitle = '팀장'): string {
  if (s.status === '완료' && s.result) return resultSentence(s.result_word, s.result === 'yes');
  if (s.status === '완료') return s.replied_by ? `${s.replied_by === 'FP' ? fpTitle : s.replied_by === '업체' ? '대표' : s.replied_by}님이 답했어요` : '끝났어요';
  if (s.retry && (s.status === '대기' || s.later)) return '다시 신청할 예정이에요';
  if (s.status === '진행' && s.later) return '시작하기 전이에요';
  if (s.status === '진행') return '지금 단계예요';   // 누가 하는지 단정하지 않음(2026-10-28)
  if (s.status === '회신대기') {
    if (s.claimed_by) {
      const by = s.claimed_by === 'FP' ? (who === 'fp' ? `${fpTitle}님이` : `담당 ${fpTitle}님이`) : who === 'customer' ? '' : '대표님이';
      return `${by ? by + ' ' : ''}${md(s.claimed_at)}에 답을 보내셨어요. ${jido}가 확인하고 있어요`;
    }
    // 팀장님께는 대표님께 안내한 글 그대로 + 아직 안 받은 서류(2026-10-28)
    if (who === 'fp' && (s.wait_question || (s.wait_items && s.wait_items.length))) return [s.wait_question || '', s.wait_items && s.wait_items.length ? `받을 서류 · ${s.wait_items.join(' · ')}` : ''].filter(Boolean).join(' — ');
    return `${whoWaits(s, who, fpTitle)} 답을 기다려요`;
  }
  if (s.fixed && who === 'fp') return '신청 마감일이에요';
  return '';
}

/** 단계 줄 오른쪽 날짜 — 끝난 날만 · FP 는 📌 공고 마감도 (지도사 목표일은 안 보임) */
export function stepDate(s: PStep, who: Audience): string {
  if (s.status === '완료') return md(s.done_at);
  if (s.fixed && who === 'fp') return md(s.due);
  // 다시 신청 — 달만 골랐으면 「11월 예정」(구체 날짜는 약속으로 읽힘 · 2026-10-18 검수) · 날짜를 직접 골랐으면 그 날
  if (s.retry && s.due && s.status !== '완료') return s.retry_month ? `${+d10(s.due).slice(5, 7)}월 예정` : md(s.due);
  // 목표일 — 2026-10-13 지도사 「목표 날짜 나와야 해」(고객 · 팀장님 모두)
  if (s.due) return md(s.due);   // 「까지」를 빼 날짜 칸을 좁힘 — 단계 이름이 꺾이고 요일 괄호가 줄마다 어긋났음(2026-10-13 검수)
  return '';
}

export function progressOf(t: PTask): { done: number; total: number; pct: number } {
  const total = t.steps.length;
  const done = t.steps.filter(s => s.status === '완료').length;
  return { done, total, pct: total ? Math.round(done / total * 100) : 0 };
}

/** 어디쯤인지 한 마디(2026-10-26 「0단계 끝났어요」가 일을 안 한 것처럼 읽혔다) — 「5단계 중 2번째」 · 아직 시작 전 · 마무리 중 */
export function stageText(t: PTask): string {
  const p = progressOf(t);
  if (t.status === '중단' && t.round_closed && p.total > 1) return `${p.total}단계 중 ${p.done}단계에서 마감`;   // 선정 안 됨 — 「n번째」면 다음 단계가 남은 것처럼 읽힘(2026-10-27)
  if (p.total <= 1) return p.done ? '마무리하고 있어요' : '한 단계 일이에요';
  if (p.done >= p.total) return `${p.total}단계 모두 끝났어요`;
  const i = t.steps.findIndex(s => s.status !== '완료');
  const cur = i >= 0 ? t.steps[i] : null;
  if (p.done === 0 && cur && (cur.status === '대기' || cur.later)) return `${p.total}단계로 진행해요`;   // 「시작 전」은 아래 「지금은 …」 줄이 말한다
  return `${p.total}단계 중 ${i + 1}번째`;
}

/** 글자 수로 줄이기 — 뒤에 붙는 말(「대표님」 · 「팀장님 × 임지도사」)이 잘리지 않게 앞 이름만 줄인다 */
export function short(s: string, n: number): string {
  const t = (s || '').trim();
  const cs = Array.from(t);   // 글자 단위(이모지 · 결합 문자) — [...t] 는 target es5 빌드에서 막힌다
  const r = cs.length > n ? cs.slice(0, n - 1).join('') + '…' : t;
  return r.replace(/^(\([^)]{1,2}\))/, '$1\u2060');   // 「(주)」 뒤에서 줄이 끊겨 한 줄을 버리지 않게
}

/** (주) · 주식회사 · 농업회사법인 등을 뺀 첫 글자 */
export function initialOf(name: string): string {
  const n = (name || '').replace(/\(주\)|\(유\)|\(합\)|주식회사|유한회사|농업회사법인|영농조합법인|사단법인|재단법인/g, '').trim();
  return n.charAt(0) || (name || '?').charAt(0);
}

/**
 * 업무 카드 「최근 소식」 — 접수 첫 소식(「팀장님이 접수하셨어요」)은 1단계 「요청 접수 · 끝났어요」와 같은 말이라 빼고 진짜 새 소식만
 *  (2026-10-28 지도사 「이 두 줄 내용도 연관성이 없어서 고민하게 되네」 — 「지금은 … 하고 있어요」 아래 엉뚱한 접수 소식)
 */
export function taskNews(t: { news?: { day: string; text: string } | null }, label: (day: string) => string): { day: string; text: string; label: string } | null {
  const n = t.news;
  if (!n || !n.text) return null;
  if (/^(팀장님이|대표님이 직접|대표님이) 접수하셨어요$/.test(n.text.trim())) return null;
  return { day: n.day, text: n.text, label: label(n.day) };
}
